import { Monitor, MapPin, Clock, Activity, Wifi, WifiOff } from 'lucide-react';

const KioskCard = ({ kiosk, onClick }) => {
  const isOnline = kiosk?.isOnline || false;

  // Format last seen time
  const formatLastSeen = (date) => {
    if (!date) return 'Never';
    const lastSeen = new Date(date);
    const now = new Date();
    const diffMs = now - lastSeen;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  return (
    <div
      onClick={() => onClick && onClick(kiosk)}
      className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-gray-100 cursor-pointer hover:scale-105"
    >
      {/* Card Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-lg ${isOnline ? 'bg-green-100' : 'bg-gray-100'}`}>
              <Monitor className={`w-8 h-8 ${isOnline ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {kiosk?.kioskName || 'Unnamed Kiosk'}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                  {kiosk?.kioskCode || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex items-center gap-2">
            {isOnline ? (
              <>
                <Wifi className="w-4 h-4 text-green-600" />
                <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                  Online
                </span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-gray-400" />
                <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-3 py-1 rounded-full">
                  Offline
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-6 bg-gray-50">
        <div className="space-y-3">
          {/* Location */}
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500 font-medium">Location</p>
              <p className="text-sm text-gray-900 font-semibold">
                {kiosk?.location || 'Not specified'}
              </p>
            </div>
          </div>

          {/* Device ID */}
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500 font-medium">Device ID</p>
              <p className="text-sm text-gray-700 font-mono">
                {kiosk?.deviceId ? kiosk.deviceId.substring(0, 20) + '...' : 'N/A'}
              </p>
            </div>
          </div>

          {/* Last Seen */}
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500 font-medium">Last Seen</p>
              <p className="text-sm text-gray-900 font-semibold">
                {formatLastSeen(kiosk?.lastSeen)}
              </p>
            </div>
          </div>

          {/* IP Address (if available) */}
          {kiosk?.ipAddress && (
            <div className="flex items-center gap-3">
              <Wifi className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 font-medium">IP Address</p>
                <p className="text-sm text-gray-700 font-mono">
                  {kiosk.ipAddress}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card Footer */}
      <div className="p-4 bg-white border-t border-gray-100">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Status: {kiosk?.isActive ? 'Active' : 'Inactive'}</span>
          <span>Created: {kiosk?.createdAt ? new Date(kiosk.createdAt).toLocaleDateString() : 'N/A'}</span>
        </div>
      </div>
    </div>
  );
};

export default KioskCard;
